import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, AbstractControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import * as moment from 'moment';
import { forkJoin, merge } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { sweetAlert } from 'src/app/utils/swal';
import { nonZeroCoordinateValidator, checkRole, findServiceArea, getAddressDetails, getstandingOrderDays, parseDisplayName } from 'src/app/utils/utils.common';
import { AssigTripsService } from '../../assigned-trips/assigntrips-service/assigntrips-service';
import { CorporateService } from '../../corporate-user/corporate-service/corporate-service';
import { LevelOfServiceService } from '../../level-of-service/service/level-of-service.service';
import { MemberService } from '../../members/member-service/member.service';
import { EquipmentService } from '../../equipments/equipment-service/equipment.service';

@Component({
  selector: 'app-new-taxi-ride',
  templateUrl: './new-taxi-ride.component.html',
  styleUrls: ['./new-taxi-ride.component.css']
})
export class NewTaxiRideComponent implements OnInit, OnDestroy {
  @Input() tripId;
  @Input() isHideUpdateAction?;
  @Output() onUpdate = new EventEmitter<string>();

  newTripForm: FormGroup;
  submitted = false;

  user;
  timeZone = "America/New_York";
  todayDate = moment().tz(this.timeZone);


  preCooperates = {};
  fundingSourceList = [];
  

  preLevelOfServices = {};
  levelOfServiceList = [];

  preSpaceTypes = {};
  spaceTypeList = [];
  spaceTypeMainList = [];
  fundingSourceSpaceTypes = [];
  
  search = '';
  assignedTrips = [];

  faresByVehicleKey = {};
  addressPickerObj;

  paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'card', label: 'Card' },
    { value: 'voucher', label: 'Voucher' },
  ];

  addonsList = [];
  filteredAddonsList = [];

  preFares = {};
  legsFare = [];

  get grandTotal(): number {
    const spaceTypeId = this.newTripForm.get('spaceType').value;
    if (!spaceTypeId || !this.legsFare.filter(Boolean).length) {
      return 0;
    }
    let total = 0;
    if (this.preFares && this.preFares[spaceTypeId]) {
      total += Number(this.preFares[spaceTypeId].totalFare) || 0;
    }
    if (this.legsFare && this.legsFare.length) {
      for (const leg of this.legsFare) {
        if (leg && leg[spaceTypeId]) {
          total += Number(leg[spaceTypeId].totalFare) || 0;
        }
      }
    }
    return total;
  }

  isSendFare = false;
  tripDetails: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private sharedDataService: SharedDataService,
    private assignTripService: AssigTripsService,
    private memberService: MemberService,
    private corporateService: CorporateService,
    private levelOfServiceService: LevelOfServiceService,
    private equipmentService: EquipmentService
  ) {
  }

  ngOnInit() {
    this.newTripForm = this.fb.group({
      company: ['', Validators.required],
      jobOriginAddress: ['', Validators.required],
      jobOriginLatitude: [0, [Validators.required, nonZeroCoordinateValidator()]],
      jobOriginLongitude: [0, [Validators.required, nonZeroCoordinateValidator()]],

      jobDestinationAddress: ['', Validators.required],
      jobDestinationLatitude: [0, [Validators.required, nonZeroCoordinateValidator()]],
      jobDestinationLongitude: [0, [Validators.required, nonZeroCoordinateValidator()]],
      
      client: [''],
      searchMember: ['', Validators.required],

      los: ['', Validators.required],
      spaceType: ['', Validators.required],
      fundingSourceSpaceTypes: [[] as any[]],

      priorityClient: this.fb.group({
        displayName: [''],
        contactNumber: ['', Validators.required],
        // cellPhone: ['']
      }),

      cooperate: ['', Validators.required],
      cooperateDetails: [{}],
      
      scheduleTime: ["", Validators.required],     
      preScheduleTime: ["", Validators.required],
      appointmentTime: ["", Validators.required],
      preAppointmentTime: ["", Validators.required],
      
      paymentMethod: ['cash', Validators.required],
      companyNote: [''],
      tripLegs: this.fb.array([]),
      addonServices: this.fb.array([]),
      companyType: ['taxi']
    });

    this.makeCompulsoryCalls();
    this.formControlsListeners();
  }

  formControlsListeners() {
    const watchFareControls = (controls: AbstractControl[]) => {
      merge(...controls.map(control => control.valueChanges))
        .pipe(
          debounceTime(500),
          takeUntil(componentDestroyed(this))
        )
        .subscribe(() => {
          this.getFare();
        });
    };

    watchFareControls(
      [
      this.newTripForm.get('cooperate'),
      this.newTripForm.get('scheduleTime'),
      this.newTripForm.get('jobOriginAddress'),
      this.newTripForm.get('jobOriginLatitude'),
      this.newTripForm.get('jobOriginLongitude'),
      this.newTripForm.get('jobDestinationAddress'),
      this.newTripForm.get('jobDestinationLatitude'),
      this.newTripForm.get('jobDestinationLongitude'),
      this.newTripForm.get('addonServices'),
      this.newTripForm.get('fundingSourceSpaceTypes')
      ].filter((c): c is AbstractControl => c !== null)
    );

  }

  setFormFromInputTrip(data) {
    console.log(data);
    const preData = JSON.parse(JSON.stringify(data));

    this.newTripForm.patchValue({
      ...preData,
      preScheduleTime: moment(preData.scheduleTime).tz(this.timeZone),
      preAppointmentTime: moment(preData.appointmentTime).tz(this.timeZone)
    });
  }

  makeCompulsoryCalls() {
    const requests = [
      this.getLevelOfService(),
      this.getSpaceTypeList(),
    ];

    // Make all requests in parallel
    forkJoin(requests).subscribe(
      ([res1, res2]) => {

        if (res1) {
          this.levelOfServiceList = res1;
          this.preLevelOfServices = res1.reduce((obj, item) => (obj[item._id] = item, obj), {});
        }
        if (res2) {
          this.spaceTypeMainList = res2;
          this.preSpaceTypes = res2.reduce((obj, item) => (obj[item._id] = item, obj), {});
        }

        this.getFundingSourceList();
        this.getUser();

      },
      error => {
        sweetAlert('Error', error, 'error', 'OK');
      }
    );
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(user => {
        if (user) {
          this.user = user;
          this.timeZone = this.user.timeZone;
          this.todayDate = moment().tz(this.timeZone);

          this.newTripForm.patchValue({
            company: this.user._id,

            preScheduleTime: this.todayDate,
            scheduleTime: this.todayDate.toISOString(),

            preAppointmentTime: moment(this.todayDate).add(30, 'minutes'),
            appointmentTime: moment(this.todayDate).add(30, 'minutes').toISOString(),
          });

          if (this.tripId) {
            this.getTripDetails();
          }
        }
      });
  }

  getLevelOfService() {
    return this.levelOfServiceService.getLevelOfServices({ status: 'active' });
  }

  getSpaceTypeList() {
    return this.levelOfServiceService.getSpaceTypes({ status: 'active' });
  }

  getFundingSourceList() {
    this.corporateService
      .getFundingSourcesByType({accountStatus: 'active', companyType: 'taxi'})
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          this.preCooperates = data.reduce((obj, item) => (obj[item._id] = item, obj), {});
          this.fundingSourceList = data.filter(x => !x.isTripModificationNotAllowed);
          if (this.fundingSourceList && this.fundingSourceList.length === 1) {
            const fundingSource = this.fundingSourceList[0]
            this.newTripForm.patchValue({
              cooperate: fundingSource._id
            });
            this.onSelectFundingSource(fundingSource._id);
          }
        }
      }, err => {
        sweetAlert('Error', err, 'warning', 'OK');
      });
  }

  getTripDetails() {
    this.assignTripService
      .getUnassignedTripDetailById(this.tripId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          this.tripDetails = data;
          this.preparedDatePatch(data)
          this.getFundingSourceDetails();
        }
      }, err => {
        sweetAlert('Error', err, 'error', 'OK');
      });
  }

  preparedDatePatch(data: any) {
    const preData = JSON.parse(JSON.stringify(data));

    this.newTripForm.patchValue({
      ...preData,
      searchMember: preData.priorityClient.displayName,
      preScheduleTime: moment(preData.scheduleTime).tz(this.timeZone),
      preAppointmentTime: moment(preData.appointmentTime).tz(this.timeZone),
      priorityClient: {
        ...preData.priorityClient
      }
    });

    if (data.cooperate) {
      this.getAddonsList(data.cooperate);
    }

    if (data.addonServices) {
      this.addAddonsItem(data.addonServices)
    }
  }

  onSelectFundingSource(event) {
    this.newTripForm.patchValue({
      cooperateDetails: {
        displayName: '',
        profileImageURL: ''
      }
    });
    this.fundingSourceSpaceTypes = [];
    this.addonsList = [];
    this.filteredAddonsList = [];
    if (event) {
      const fundingSource = this.fundingSourceList.find((fundingSource) => fundingSource._id === event);
      if (fundingSource) {
        this.getFundingSourceDetails();
        this.getAddonsList(fundingSource._id);
        this.newTripForm.patchValue({
          cooperateDetails: {
            displayName: fundingSource.displayName,
            profileImageURL: fundingSource.profileImageURL
          }
        });
      }
    }
  }

  getFundingSourceDetails() {
    this.corporateService.getCorporateUserById(this.newTripForm.value.cooperate)
    .pipe(takeUntil(componentDestroyed(this)))
    .subscribe((data) => {
      if (data && data.spaceTypes && data.spaceTypes.length) {
        this.fundingSourceSpaceTypes = data.spaceTypes
          .map((spaceType) => {
            const preSpaceType = this.preSpaceTypes[spaceType];
            const preLevelOfService = this.preLevelOfServices[preSpaceType.los];
            
            return {
              ...preSpaceType,
              profileImageURL: preLevelOfService.profileImageURL
            }
          });
      }
      this.newTripForm.patchValue({ fundingSourceSpaceTypes: this.fundingSourceSpaceTypes });
    });

  }

  getFare(legIndex?) {
    const legs = this.newTripForm.get('tripLegs') as FormArray;

    const {
      cooperate,
      addonServices,
      fundingSourceSpaceTypes
    } = this.newTripForm.value;

    let formValues = legIndex >= 0 ? legs.at(legIndex).value : this.newTripForm.value;

    const { 
      scheduleTime, 
      jobOriginLatitude, 
      jobOriginLongitude, 
      jobDestinationLatitude, 
      jobDestinationLongitude,
    } = formValues;

    if (!scheduleTime || !jobOriginLatitude ||  !jobOriginLongitude || !jobDestinationLatitude || !jobDestinationLongitude || !cooperate || !fundingSourceSpaceTypes.length) {
      if (legIndex >= 0) {
        if (this.legsFare[legIndex]) {
          this.legsFare[legIndex] = null;
        }
      } else {
        this.preFares = {}; 
      }
      return;
    }

    const payload = {
      cooperate,
      scheduleTime, 
      jobOriginLatitude, 
      jobOriginLongitude, 
      jobDestinationLatitude, 
      jobDestinationLongitude,
      spaceTypes: fundingSourceSpaceTypes.map(sp => sp._id),
      addonServices,
      passengerNumber: 0,
    };

    if (this.tripId && !this.isSendFare) {
      this.isSendFare = true;
      payload['estimatedFare'] = this.tripDetails.preSpecialRate;
    }

    if (addonServices && addonServices.length) {
      const addons = addonServices.filter((item: any) => item.addonId);
      if (addons && addons.length) {
        payload['addonServices'] = addonServices;
      } else {
        payload['addonServices'] = [];
      }
    } else {
      payload['addonServices'] = [];
    }

    this.assignTripService
      .calculateEstimateTaxiFare(payload)
      .subscribe(data => {
        if (data && data.length) {
          if (legIndex >= 0) {
            this.legsFare[legIndex] = data.reduce((obj, item) => (obj[item.spaceType] = item, obj), {});
          } else {
            this.preFares = data.reduce((obj, item) => (obj[item.spaceType] = item, obj), {});
            if (!this.tripId && !this.newTripForm.value.spaceType) {
              const spaceType = fundingSourceSpaceTypes.find((spaceType: any) => (this.preFares as any)[spaceType._id]);
              if (spaceType) {
                this.onSelectSpaceType(spaceType._id);
              }
            }
          }
        }
      });
  }

  getAddonsList(fundingSourceId) {
    let queryParams = {
      isAddon: true,
      cooperate: fundingSourceId
    }
    this.equipmentService
      .getEquipments(queryParams)
      .subscribe(data => {
        if (data) {
          this.addonsList = data;
          this.filteredAddonsList = data;
        }
      }, err => {
        sweetAlert("Error", err, "error", "Ok");
      });
  }

  onSelectMember(member) {
    if (member) {
      const { homeAddress } = member;
      this.newTripForm.patchValue({
        client: member._id,
        priorityClient: {
          displayName: member.displayName,
          contactNumber: member.contactNumber
        },
        searchMember: member.displayName,
      });

      if (!this.tripId && !this.newTripForm.value.jobOriginAddress && homeAddress && homeAddress.address) {
        this.newTripForm.patchValue({
          jobOriginAddress: homeAddress.address,
          jobOriginLatitude: homeAddress.latitude,
          jobOriginLongitude: homeAddress.longitude
        });
      }

      this.getMemberTrips(member);
    } else {
      this.newTripForm.patchValue({
          client: '',
          priorityClient: {
            displayName: '',
            contactNumber: ''
          }
        });
    }
  }

  onSelectSpaceType(spaceTypeId) {
    if(!this.preFares[spaceTypeId]) {
      return;
    }
    this.newTripForm.patchValue({ los: '' });
    this.newTripForm.patchValue({ spaceType: spaceTypeId });
    if (this.newTripForm.value.spaceType) {
      const selectedSpaceType = this.spaceTypeMainList.find(item => item._id === this.newTripForm.value.spaceType);
      if (selectedSpaceType && selectedSpaceType.los) {
        this.newTripForm.patchValue({
          los: selectedSpaceType.los,
        });
      }
    }
  }

  onSelectPaymentMethod(method: string) {
    this.newTripForm.patchValue({ paymentMethod: method });
  }

  setOriginAddress(place, index?) {
    if (index > -1) {
      const items = this.newTripForm.get('tripLegs') as FormArray;
      items.at(index).patchValue({
        jobOriginAddress: place.formatted_address,
        jobOriginLatitude: place.geometry.location.lat(),
        jobOriginLongitude: place.geometry.location.lng()
      });
    } else {
      this.newTripForm.patchValue({
        jobOriginAddress: place.formatted_address,
        jobOriginLatitude: place.geometry.location.lat(),
        jobOriginLongitude: place.geometry.location.lng()
      });
    }
  }

  setDestinationAddress(place, index?) {
    if (index > -1) {
      const items = this.newTripForm.get('tripLegs') as FormArray;
      items.at(index).patchValue({
        jobDestinationAddress: place.formatted_address,
        jobDestinationLatitude: place.geometry.location.lat(),
        jobDestinationLongitude: place.geometry.location.lng()
      });
    } else {
      this.newTripForm.patchValue({
        jobDestinationAddress: place.formatted_address,
        jobDestinationLatitude: place.geometry.location.lat(),
        jobDestinationLongitude: place.geometry.location.lng()
      });
    }
  }

  loadAddressPicker(title, value, address?, lat?, lng?, zipCode?) {
    this.addressPickerObj = { title, value, address, lat, lng, zipCode };
  }

  onAddressPicker(event) {
    this.addressPickerObj = null;
    const items = this.newTripForm.get('tripLegs') as FormArray;
    if (!event) return;

    // For Pickup Addresss
    if (event.reference.value === '0') {
      this.newTripForm.patchValue({
        jobOriginAddress: event.address,
        jobOriginLatitude: event.lat,
        jobOriginLongitude: event.lng
      });
    } else if (event.reference.value === '1') {
      this.newTripForm.patchValue({
        jobDestinationAddress: event.address,
        jobDestinationLatitude: event.lat,
        jobDestinationLongitude: event.lng
      });
    } else if (event.reference.value === '01') {
      items.at(0).patchValue({
        jobOriginAddress: event.address,
        jobOriginLatitude: event.lat,
        jobOriginLongitude: event.lng
      });
    } else if (event.reference.value === '11') {
      items.at(0).patchValue({
        jobDestinationAddress: event.address,
        jobDestinationLatitude: event.lat,
        jobDestinationLongitude: event.lng
      });
    } else if (event.reference.value === '02') {
      items.at(1).patchValue({
        jobOriginAddress: event.address,
        jobOriginLatitude: event.lat,
        jobOriginLongitude: event.lng
      });
    } else if (event.reference.value === '12') {
      items.at(1).patchValue({
        jobDestinationAddress: event.address,
        jobDestinationLatitude: event.lat,
        jobDestinationLongitude: event.lng
      });
    }
  }

  setPickTimeChange(event, index?) {
    if (index > -1) {
      const items = this.newTripForm.get('tripLegs') as FormArray;
      items.at(index).patchValue({
        scheduleTime: moment(event).tz(this.timeZone, true).toISOString(),
      });

      if (!this.tripId) {
        if (moment(event).tz(this.timeZone).format("HHmm") == '2359') {
          items.at(index).patchValue({
            preAppointmentTime: moment(event)
          });
        } else {
          items.at(index).patchValue({
            preAppointmentTime: moment(event).add(30, 'minutes')
          });
        }
      }

    } else {
      this.newTripForm.patchValue({
        scheduleTime: moment(event).tz(this.timeZone, true).toISOString(),
      });

      if (!this.tripId) {
        this.newTripForm.patchValue({
          preAppointmentTime: moment(event).add(30, 'minutes')
        });
      }
    }
  }

  setDropOffTimeChange(event, index?) {
    if (index > -1) {
      const items = this.newTripForm.get('tripLegs') as FormArray;
      items.at(index).patchValue({
        appointmentTime: moment(event).tz(this.timeZone, true).toISOString(),
      });

    } else {
      this.newTripForm.patchValue({
        appointmentTime: moment(event).tz(this.timeZone, true).toISOString(),
      });
    }
  }

  onSelectAddonItem(addonItem) {
    if (!addonItem.value.addonId || !addonItem.value.quantity) return;

    const item = this.addonsList.find(item => item._id === addonItem.value.addonId);
    addonItem.patchValue({
      ...item,
      total: item.fare * addonItem.value.quantity
    });
  }
  
  async onCreateTrip() {
    this.submitted = true;
    this.runValidationsChecks(); // calling order matters
    if (this.newTripForm.invalid) return;

    if (this.newTripForm.value.searchMember && !this.newTripForm.value.client) {
      if (!this.isMemberValid()) return;
      const member = await this.createMember();
      if (!member) return;
      this.newTripForm.patchValue({
        client: member._id,
        priorityClient: {
          displayName: member.displayName
        }
      });
    }

    if (!this.newTripForm.value.priorityClient.contactNumber) {
      this.newTripForm.controls.priorityClient.patchValue({
        contactNumber: '0000000000'
      });
    }

    let payload = this.newTripForm.value;
    delete payload.fundingSourceSpaceTypes;

    this.assignTripService
      .createNewTripV2(payload, this.tripId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          if (this.tripId) {
            this.onUpdate.emit(data);
          } else {
            this.router.navigateByUrl('trips');
          }
        }
      }, err => {
        sweetAlert('Error', err, 'error', 'OK');
      });
  }

  runValidationsChecks() {
    if (this.newTripForm.value.cooperate && !this.fundingSourceSpaceTypes.length) {
      sweetAlert('Error', 'No level of service found against the selected funding source.', 'error', 'OK');
    }

    if (this.newTripForm.value.cooperate && 
        this.fundingSourceSpaceTypes.length &&
        this.newTripForm.value.scheduleTime &&
        this.newTripForm.value.jobOriginLatitude &&
        this.newTripForm.value.jobOriginLongitude &&
        this.newTripForm.value.jobDestinationLatitude &&
        this.newTripForm.value.jobDestinationLongitude &&
        !this.newTripForm.value.spaceType) {
      sweetAlert('Error', 'No fare was found against the level of service(s)', 'error', 'OK');
    }
  }

  isMemberValid(): boolean {
    const member = this.newTripForm.value.searchMember.trim();
    if (!member) {
      sweetAlert('Error', 'Member is required', 'error', 'OK');
      return false;
    }
    const { firstName, middleName, lastName } = parseDisplayName(member);
    if (!lastName) {
      sweetAlert('Error', 'Member\'s last name is required', 'error', 'OK');
      return false;
    }
    return true;
  }

  async createMember() {
    const member = this.newTripForm.value.searchMember.trim();
    const { firstName, middleName, lastName } = parseDisplayName(member);
    const payload = {
      firstName,
      middleName,
      lastName,
      contactNumber: this.newTripForm.value.priorityClient && this.newTripForm.value.priorityClient.contactNumber
        ? this.newTripForm.value.priorityClient.contactNumber
        : '',
      homeAddress: {
        address: this.newTripForm.value.jobOriginAddress,
        latitude: this.newTripForm.value.jobOriginLatitude,
        longitude: this.newTripForm.value.jobOriginLongitude
      },
      clientType: 'patient',
      fundingSource: this.newTripForm.value.cooperate
    };
    try {
      return await this.memberService.saveMember(null, payload).toPromise();
    } catch (err) {
      sweetAlert('Error', err, 'error', 'OK');
      return null;
    }
  }

  getMemberTrips(member) {
    this.assignedTrips = [];
    const payload = {
      client: member._id,
    };
    this.assignTripService
      .getMemberTrips(payload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          this.assignedTrips = data;
        }
      });
  }

  onSelectHistoryTrip(data) {
    this.preparedDatePatch(data);
  }

  addLegItem() {
    const items = this.newTripForm.get('tripLegs') as FormArray;
    const preValues = this.newTripForm.value;
    let minutes = ((items.length * 2) + 1) * 30;

    const FIELD_MAP = [
      { prev: 'jobDestinationAddress', new: 'jobOriginAddress' },
      { prev: 'jobDestinationLatitude', new: 'jobOriginLatitude' },
      { prev: 'jobDestinationLongitude', new: 'jobOriginLongitude' }
    ];

    const newLeg = this.fb.group({
      jobDestinationAddress: [preValues.jobOriginAddress, Validators.required],
      jobDestinationLatitude: [preValues.jobOriginLatitude, [Validators.required, nonZeroCoordinateValidator()]],
      jobDestinationLongitude: [preValues.jobOriginLongitude, [Validators.required, nonZeroCoordinateValidator()]],

      jobOriginAddress: [preValues.jobDestinationAddress, Validators.required],
      jobOriginLatitude: [preValues.jobDestinationLatitude, [Validators.required, nonZeroCoordinateValidator()]],
      jobOriginLongitude: [preValues.jobDestinationLongitude, [Validators.required, nonZeroCoordinateValidator()]],

      preScheduleTime: [moment(preValues.preAppointmentTime).add(minutes, 'minutes'), Validators.required],
      scheduleTime: [moment(preValues.preAppointmentTime).add(minutes, 'minutes').toISOString(), Validators.required],

      preAppointmentTime: [moment(preValues.preAppointmentTime).add(minutes + 30, 'minutes'), Validators.required],
      appointmentTime: [moment(preValues.preAppointmentTime).add(minutes + 30, 'minutes').toISOString(), Validators.required]
    });

    items.push(newLeg);

    if (items.length > 1) {
      const prevLeg = items.at(items.length - 2) as FormGroup;
      const currentLeg = items.at(items.length - 1) as FormGroup;

      currentLeg.patchValue({
        jobOriginAddress: prevLeg.value.jobDestinationAddress || '',
        jobOriginLatitude: prevLeg.value.jobDestinationLatitude || 0,
        jobOriginLongitude: prevLeg.value.jobDestinationLongitude || 0,

        jobDestinationAddress: '',
        jobDestinationLatitude: 0,
        jobDestinationLongitude: 0
      });

      // const createLink = (prevPath: string, newPath: string) => {
      //   prevLeg.get(prevPath).valueChanges.subscribe(value =>
      //     currentLeg.get(newPath).patchValue(value, { emitEvent: true })
      //   )
      // };

      // FIELD_MAP.forEach(({ prev, new: newPath }) => createLink(prev, newPath));

      // if (items.length > 1) {
      //   const resetValues = {
      //     jobDestinationAddress: null,
      //     jobDestinationLatitude: null,
      //     jobDestinationLongitude: null
      //   };
      //   prevLeg.patchValue(resetValues);
      // }
    }

    const watchFareControls = (controls: AbstractControl[]) => {
      merge(...controls.map(control => control.valueChanges))
        .pipe(
          debounceTime(500),
          takeUntil(componentDestroyed(this))
        )
        .subscribe(() => {
          const currentIndex = items.controls.indexOf(newLeg);
          if (currentIndex !== -1) {
            this.getFare(currentIndex);
          }
        });
    };

    watchFareControls(
      [
        this.newTripForm.get('cooperate'),
        newLeg.get('scheduleTime'),
        newLeg.get('jobOriginAddress'),
        newLeg.get('jobOriginLatitude'),
        newLeg.get('jobOriginLongitude'),
        newLeg.get('jobDestinationAddress'),
        newLeg.get('jobDestinationLatitude'),
        newLeg.get('jobDestinationLongitude'),
        this.newTripForm.get('addonServices')
      ].filter((c): c is AbstractControl => c !== null)
    );

    this.getFare(items.length - 1)
  }

  removeLegItem(index: any) {
    const items = this.newTripForm.get('tripLegs') as FormArray;
    items.removeAt(index);
    if (this.legsFare && this.legsFare.length > index) {
      this.legsFare.splice(index, 1);
    }
  }

  addAddonsItem(preAddonList?) {
    const items = this.newTripForm.get('addonServices') as FormArray;

    if (preAddonList) {
      for (const addon of preAddonList) {
        items.push(
          this.fb.group({
            addonId: [addon.addonId, Validators.required],
            quantity: [addon.quantity, [Validators.required, Validators.min(1)]],
            title: [addon.title],
            fare: [addon.fare],
            code: [addon.code],
            total: [addon.total]
          })
        );

      }
      return;
    }

    // if (!items.invalid) {
    items.push(
      this.fb.group({
        addonId: ['', Validators.required],
        quantity: [1, [Validators.required, Validators.min(1)]],
        title: [''],
        fare: [0],
        code: [0],
        total: [0]
      })
    );
    // }
  }

  removeAddonsItem(index: any) {
    const items = this.newTripForm.get('addonServices') as FormArray;
    items.removeAt(index);
  }

  get addonsForm() {
    return this.newTripForm.get('addonServices') as FormArray;
  }

  ngOnDestroy(): void {
  }

  get form() {
    return this.newTripForm.controls;
  }

  get legForm() {
    return this.newTripForm.get('tripLegs') as FormArray;
  }
}
